import { ElapsedTimer } from "@/components/status/ElapsedTimer";
import { store } from "@/store";
import { portfolioExportActions } from "@/store/slices/portfolioExport";
import { selectSummaryStatusModal } from "@/store/slices/portfolioExport/selectors";
import { withCommas } from "@/utils/number";
import { ClockCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { Modal, Tag } from "antd";
import { ForwardedRef, forwardRef, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AdaptationSummaryWithProgress, SummaryWithProgress } from "../schema";
type SummaryStatusProps = {
  summaries?: SummaryWithProgress[];
  adaptationSummaries?: AdaptationSummaryWithProgress[];
  defaultOpen?: boolean;
};
type ActiveSummaryStatusItem = {
  id: string | null;
  summary_id: string | null;
  summary_started_at: string | null;
  name: string;
  success: number | null;
  error: number | null;
  group_name: string | null;
  status: string;
  position: number | null;
  summary_type: "portfolio" | "adaptation";
};
export const SummaryStatusModal = forwardRef(({
  summaries = undefined,
  adaptationSummaries = undefined,
  defaultOpen = false
}: SummaryStatusProps, ref: ForwardedRef<null>) => {
  const {
    t
  } = useTranslation("terms");
  const {
    isOpen,
    highlightedSummaryId
  } = useSelector(selectSummaryStatusModal);
  const handleCancel = () => {
    store.dispatch(portfolioExportActions.setSummaryStatusModal({
      isOpen: false
    }));
  };
  const allSummaries = useMemo((): ActiveSummaryStatusItem[] => {
    const summaryItems: ActiveSummaryStatusItem[] = (summaries ?? []).map(s => ({
      id: s.id ?? null,
      summary_id: s.summary_id,
      summary_started_at: s.summary_started_at,
      name: s.name ?? "",
      success: s.success,
      error: s.error,
      group_name: s.group_name,
      status: s.status,
      position: s.position,
      summary_type: "portfolio"
    }));
    const adaptationSummaryItems: ActiveSummaryStatusItem[] = (adaptationSummaries ?? []).map(a => ({
      id: a.id,
      summary_id: a.summary_id,
      summary_started_at: a.summary_started_at,
      name: a.name,
      success: a.success,
      error: a.error,
      group_name: a.group_name,
      status: a.status,
      position: a.position,
      summary_type: "adaptation"
    }));
    return [...summaryItems, ...adaptationSummaryItems].filter(item => item.status !== "complete").sort((a, b) => {
      if (a.position === null) return 1;
      if (b.position === null) return -1;
      return a.position - b.position;
    });
  }, [summaries, adaptationSummaries]);
  const getStatus = (status: string, position: number) => {
    if (status === "pending" && position === 1) {
      return <Tag icon={<SyncOutlined spin />} color="processing">
            Processing
          </Tag>;
    }
    if (status === "pending") {
      return <Tag icon={<ClockCircleOutlined />} color="orange">
            Pending (#{position} in queue)
          </Tag>;
    }
    if (status === "error") {
      return <Tag icon={<CloseCircleOutlined />} color="error">
            Error
          </Tag>;
    }
    return <Tag icon={<ExclamationCircleOutlined />} data-sentry-element="Tag" data-sentry-component="getStatus" data-sentry-source-file="SummaryStatusModal.tsx">Unknown</Tag>;
  };
  return <Modal open={isOpen || defaultOpen} onCancel={handleCancel} title="Summaries in Progress" footer={null} modalRender={(node: ReactNode) => <div ref={ref}>{node}</div>}>
        <div className="flex flex-col gap-2">
          {allSummaries.map(item => <div key={`${item.summary_type}-${item.id}`} className={`rounded-lg border px-4 py-2 ${highlightedSummaryId === item.summary_id ? "border-blue-100 bg-blue-50 dark:border-blue-700 dark:bg-blue-950" : "border-subtle bg-gray-50"}`}>
              <div className="grid items-center" style={{
          gridTemplateColumns: `72px 1fr`,
          gridAutoRows: "1.75em"
        }}>
                <div className="text-gray-500">Name</div>
                <div className="flex items-center gap-2 overflow-auto">
                  <span>{item.name}</span>
                  {item.summary_type === "adaptation" && <Tag color="green" className="shrink-0">
                      {t("adaptationTypes.enhanced_resilience")}
                    </Tag>}
                </div>

                <div className="text-gray-500">Assets</div>
                <div>
                  {withCommas(item.success ?? 0)}
                  {(item.error ?? 0) > 0 && <span> ({withCommas(item.error ?? 0)} failed)</span>}
                </div>

                {item.group_name && <>
                    <div className="text-gray-500">Group</div>
                    <div>{item.group_name}</div>
                  </>}

                <div className="text-gray-500">Status</div>
                <div className="flex items-center justify-between gap-2">
                  <span>{getStatus(item.status, item.position || 0)}</span>
                  <ElapsedTimer startTime={item.summary_started_at} />
                </div>
              </div>
            </div>)}
        </div>
      </Modal>;
});